import React, {
  useState, useEffect, useRef,
} from 'react';
import styled from '@emotion/styled';
import { Global, css } from '@emotion/core';
import { batch, useSelector, useDispatch } from 'react-redux';
import Logger from 'js-logger';
import { StripeProvider } from 'react-stripe-elements';
import { useTheme } from 'emotion-theming';
import PropTypes from 'prop-types';

import { logOut } from '../../../login/ducks/loginActions';
import {
  setModalOpen, clearPaymentData, clearCoupon, clearCouponError,
} from '../../ducks/programsActions';
import Modal from '../../../../shared/components/Modal/Modal';
import Avatar from '../../../../shared/_TBComponents/Avatar/Avatar';
import HeaderText from '../../../../shared/_TBComponents/HeaderText';
import Text from '../../../../shared/_TBComponents/Text';
import useScript from '../../../../shared/hooks/useScript';
import ModalLogin from './ModalLogin';
import ModalCreateAccount from './ModalCreateAccount';
import ModalCheckout from './ModalCheckout';
import { getTestOrLiveStripeApiKey } from '../../../../shared/utils/payments';
import Button from '../../../../shared/_TBComponents/Button';
import PasswordNeeded from '../../../login/components/shared/PasswordNeeded';

const ModalContainer = styled('div')`
  justify-items: ${(props) => (props.isPasswordNeeded ? 'center' : '')};
  width: 100%;


  button {
    position: relative;
    bottom: 0px;
  }
  
  .toggle {
    display: flex;
    flex-direction: row;
    width: 100%;
  }
`;

const ModalHeader = styled('div')`
  width: 100%;
  border-bottom: 1px solid ${(props) => props.theme.colors.border};
  position: absolute;
  text-align: center;
  left: 0px;
  top: 0;
  padding: 25px;
`;

const ModalBody = styled('div')`
  margin-top: 70px;
  p {
    margin-bottom: 20px;
  }

  .login-link {
    text-align: center;
    margin-bottom: 30px;
    width: 100%;
    cursor: pointer;
    
    span {
      font-family: inherit;
      color: ${(props) => props.theme.colors.orange}
    }
    &:hover {
      opacity: .7;
    }
  }

  .login {
    font-weight: 600;
    color: #ff6600;
    &:hover {
      opacity: .7;
    }
  }
`;

const AuthenticatedCheckOut = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ProgramModal = ({
  accountLogoKey,
  resellerSlug,
  isAuthenticatedv3,
  userName,
}) => {
  const accountCode = useSelector((state) => state.auth.data.currentUser.accountCode);
  const activeModal = useSelector((state) => state.programs.ui.activeModal);
  const emailAddress = useSelector((state) => state.auth.data.emailAddress);
  const isAuthenticated = useSelector((state) => state.auth.ui.isAuthenticated);
  const isModalOpen = useSelector((state) => state.programs.ui.isModalOpen);
  const isPasswordNeeded = useSelector((state) => state.auth.data.isPasswordNeeded);
  const isRegisteredInClient = useSelector((state) => state.auth.ui.isRegisteredInClient);

  const [modalDimensions, setModalDimensions] = useState({});

  const theme = useTheme();

  useScript('https://js.stripe.com/v3/');

  const modalContentRef = useRef(null);

  const dispatch = useDispatch();

  // Check if we have authenticated when isAuthenticated changes, should only be
  // after a user logs in. If so, close login modal, open checkout modal.
  useEffect(() => {
    if (isAuthenticated) {
      dispatch(setModalOpen(false, 'login'));
      setTimeout(() => {
        dispatch(setModalOpen(true, 'checkout'));
      }, 800);
    }
  }, [isAuthenticated]);

  // Check if we have registered when isRegisteredInClient changes, should only be
  // after a user registers. If so, close createaccount modal, open checkout modal.
  useEffect(() => {
    if (isRegisteredInClient) {
      dispatch(setModalOpen(false, 'createAccount'));
      setTimeout(() => {
        dispatch(setModalOpen(true, 'checkout'));
      }, 800);
    }
  }, [isRegisteredInClient]);

  // To make the modal scrollable when the window height is small, we save the
  // modal dimensions on isOpen, and set the height dynamically in a css media query
  // where we make the modal absolute position, and overflow auto.
  useEffect(() => {
    setTimeout(() => {
      Logger.debug('Modal height:', modalContentRef.current.clientHeight);
      setModalDimensions(modalContentRef.current.clientHeight);
    }, 2000);
  }, [isModalOpen]);

  const closeModal = (modal) => {
    dispatch(clearCouponError());
    dispatch(clearCoupon());
    dispatch(setModalOpen(false, modal));
  };

  const openModal = (modal) => {
    closeModal(activeModal);
    setTimeout(() => {
      dispatch(setModalOpen(true, modal));
    }, 800);
  };

  const openLogin = (e) => {
    e.preventDefault();
    batch(() => {
      dispatch(logOut());
      dispatch(clearPaymentData());
      dispatch(setModalOpen(true, 'login'));
    });
  };

  return (
    <>
      <Global
        styles={css`
          .ReactModal__Overlay {
            overflow: auto;
          }

          @media screen and (max-height: ${modalDimensions + 80}px) {
            .ReactModal__Content {
              position: absolute!important;
              top: 15px!important;
            }
          }

        `}
      />
      <Modal
        onRequestClose={closeModal}
        isOpen={isModalOpen}
        customButton
      >
        <ModalContainer
          isPasswordNeeded={isPasswordNeeded}
          ref={modalContentRef}
        >
          {activeModal === 'login' && (
            isPasswordNeeded ? (
              <PasswordNeeded emailAddress={emailAddress} />
            ) : (
              <>
                <ModalHeader>
                  <HeaderText>Sign In</HeaderText>
                </ModalHeader>
                <ModalBody>
                  <ModalLogin
                    openModal={openModal}
                  />
                </ModalBody>
              </>
            )
          )}
          {activeModal === 'createAccount' && (
            <>
              <ModalHeader>
                <HeaderText>Create Account</HeaderText>
              </ModalHeader>
              <ModalBody>
                <ModalCreateAccount
                  openModal={openModal}
                  resellerSlug={resellerSlug}
                />
              </ModalBody>
            </>
          )}
          {activeModal === 'checkout' && (
            <>
              <ModalHeader>
                <HeaderText>Checkout</HeaderText>
              </ModalHeader>
              <ModalBody>
                {(isAuthenticated || isAuthenticatedv3) && (
                  <AuthenticatedCheckOut>
                    <Avatar
                      avatar={accountLogoKey}
                      name={userName}
                      size={25}
                    />
                    <Text styledTextProps={{ fontSize: theme.textFontSizes[0], marginBottom: '0px !important' }} styledWrapperProps={{ margin: '10px 0px 0px' }}>
                      Checking out as
                      {' '}
                      {userName}
                      .
                    </Text>
                    <Button
                      fontSize={theme.textFontSizes[0]}
                      name='Use another account?'
                      noBorder
                      onClick={openLogin}
                      styledTextProps={{ letterSpacing: 'unset', marginBottom: '0px !important' }}
                      txtColor={theme.colors.orange}
                      type='button'
                    />
                  </AuthenticatedCheckOut>
                )}
                <StripeProvider apiKey={getTestOrLiveStripeApiKey(accountCode)}>
                  <ModalCheckout />
                </StripeProvider>
              </ModalBody>
            </>
          )}
        </ModalContainer>
      </Modal>
    </>
  );
};

ProgramModal.propTypes = {
  accountLogoKey: PropTypes.string.isRequired,
  resellerSlug: PropTypes.string.isRequired,
  isAuthenticatedv3: PropTypes.bool.isRequired,
  userName: PropTypes.string.isRequired,
};

export default ProgramModal;
